import { useCallback, useMemo } from "react";

import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "../constants";
import { useWriterComponentState } from "./useWriterComponentState";

import type { ShipperEmailNotificationPreferenceKey } from "./useShipperEmailNotificationPreferences";

interface Props {
  onUpdateSuccess?: () => void;
  onUpdateFailure?: (error: Error) => void;
}

// Sends only the toggled key; the backend upserts it and returns the full
// overlaid state. The per-toggle body is passed to write() at action time, so
// no ref or mutable render-time object is needed.
export function useShipperEmailNotificationPreferencesUpdate(props: Props) {
  const { onUpdateSuccess, onUpdateFailure } = props;
  const posthog = usePostHog();

  const writeConfig = useMemo(
    () => ({
      method: "patch" as const,
      url: "/shipper-email-notification-preferences",
    }),
    [],
  );

  const { loading, error, write } = useWriterComponentState({
    errorMessage: "Failed to update email notification preferences",
    writeConfig,
    errorMessageKey: "message",
    onWriteFailure: onUpdateFailure,
  });

  const updatePreference = useCallback(
    (
      preferenceKey: ShipperEmailNotificationPreferenceKey,
      emailEnabled: boolean,
    ) => {
      write(
        () => {
          posthog?.capture(
            ClientAppEvent.SHIPPER_EMAIL_NOTIFICATION_PREFERENCE_TOGGLED,
            { preference_key: preferenceKey, email_enabled: emailEnabled },
          );
          if (onUpdateSuccess) {
            onUpdateSuccess();
          }
        },
        {
          preferences: [
            { preference_key: preferenceKey, email_enabled: emailEnabled },
          ],
        },
      );
    },
    [write, posthog, onUpdateSuccess],
  );

  return {
    loading,
    error,
    updatePreference,
  };
}
